/**
 * Loamify growth loop — connects creator actions to platform outcomes.
 * Structural model only; no scoring formulas implemented.
 */

/** Inputs that feed the creator growth loop. */
export interface CreatorGrowthLoopInputs {
  content_created: number;
  engagement_received: number;
  follows_received: number;
  purchases_generated: number;
}

/** Outputs produced by the growth loop (placeholder score slots). */
export interface CreatorGrowthLoopOutputs {
  visibility_score: number;
  creator_score: number;
  monetization_score: number;
}

/** Unified growth loop container. */
export interface CreatorGrowthLoop {
  inputs: CreatorGrowthLoopInputs;
  outputs: CreatorGrowthLoopOutputs;
}

export function createEmptyGrowthLoopInputs(): CreatorGrowthLoopInputs {
  return {
    content_created: 0,
    engagement_received: 0,
    follows_received: 0,
    purchases_generated: 0,
  };
}

export function createEmptyGrowthLoopOutputs(): CreatorGrowthLoopOutputs {
  return {
    visibility_score: 0,
    creator_score: 0,
    monetization_score: 0,
  };
}

export function createEmptyCreatorGrowthLoop(): CreatorGrowthLoop {
  return {
    inputs: createEmptyGrowthLoopInputs(),
    outputs: createEmptyGrowthLoopOutputs(),
  };
}

/** Maps each growth input key to its semantic meaning in the loop. */
export const GROWTH_LOOP_INPUT_DEFINITIONS: Readonly<
  Record<keyof CreatorGrowthLoopInputs, string>
> = {
  content_created: 'Creator publishes content that enters the feed visibility layer',
  engagement_received: 'Audience interaction with creator content',
  follows_received: 'Graph expansion via follow relationships',
  purchases_generated: 'Monetization events from product purchases',
};

/** Maps each growth output key to downstream systems. */
export const GROWTH_LOOP_OUTPUT_DEFINITIONS: Readonly<
  Record<keyof CreatorGrowthLoopOutputs, string>
> = {
  visibility_score: 'Feed discovery and exposure ranking input',
  creator_score: 'Composite creator health and growth signal',
  monetization_score: 'Revenue and commerce performance signal',
};
